"use client";

import { useCallback, useEffect, useState } from "react";
import {
  computeAnchoredPosition,
  visualViewportRect,
  type AnchoredPosition,
  type Placement,
} from "@/lib/anchoredPosition";

/**
 * Measures `anchorRef` and keeps a floating panel placed against it — flipping
 * sides and capping its height so it stays on screen. See
 * `lib/anchoredPosition.ts` for the maths.
 *
 * Re-measures on scroll (captured, since the anchor can live inside any
 * scrollable ancestor and scroll doesn't bubble) and on both viewport resize
 * events — the visual-viewport one is what fires when a mobile keyboard opens
 * or closes.
 */
export function useAnchoredPosition(
  anchorRef: React.RefObject<HTMLElement | null>,
  active: boolean,
  {
    preferred = "above",
    desiredHeight,
    minHeight,
    align,
    width,
    maxWidth,
    revision,
  }: {
    preferred?: Placement;
    desiredHeight: number;
    minHeight?: number;
    align?: "start" | "center";
    /** The panel's own width, for one that does not take the anchor's. */
    width?: number;
    maxWidth?: number;
    /** Re-measure when this changes — pass whatever drives the panel's content. */
    revision?: unknown;
  },
): AnchoredPosition | null {
  const [pos, setPos] = useState<AnchoredPosition | null>(null);

  const measure = useCallback(() => {
    const el = anchorRef.current;
    if (!el || !active) {
      setPos(null);
      return;
    }
    setPos(
      computeAnchoredPosition({
        anchor: el.getBoundingClientRect(),
        viewport: visualViewportRect(),
        preferred,
        desiredHeight,
        minHeight,
        align,
        width,
        maxWidth,
      }),
    );
  }, [anchorRef, active, preferred, desiredHeight, minHeight, align, width, maxWidth]);

  useEffect(() => {
    // Position comes from getBoundingClientRect, which is only meaningful
    // after layout — measurement genuinely belongs in an effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    measure();
  }, [measure, revision]);

  useEffect(() => {
    if (!active) return;
    const vv = window.visualViewport;
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    vv?.addEventListener("resize", measure);
    vv?.addEventListener("scroll", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      vv?.removeEventListener("resize", measure);
      vv?.removeEventListener("scroll", measure);
    };
  }, [active, measure]);

  return pos;
}
